import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Trash2, Edit, AlertCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { DatabaseConfigDialog, DatabaseFormData } from './database-config-dialog';
import { useListDatabaseConnections } from 'hooks/use-list-database-connections';
import { useGetDatabaseConnection } from 'hooks/use-get-database-connection';
import { useUpsertDatabaseConnection } from 'hooks/use-upsert-database-connection';
import { useDeleteDatabaseConnection } from 'hooks/use-delete-database-connection';
import { useConnectDatabaseConnection } from 'hooks/use-connect-database-connection';
import { useDisconnectDatabaseConnection } from 'hooks/use-disconnect-database-connection';
import { DatabaseConnection } from 'services/database-connection-service';
import { useToast } from 'hooks/use-toast';
import mysqlIcon from 'assets/images/mysql.svg';

/**
 * MySQL Connection Component
 * 
 * Manages MySQL database connections with a professional UI.
 * Allows users to add, edit, view, and delete MySQL configurations.
 * 
 * Features:
 * - Display existing MySQL connections
 * - Add new MySQL connections
 * - Edit existing connections
 * - Delete connections with confirmation
 * - Loading states and error handling
 * - Professional card-based UI
 * 
 * @param onBack - Callback to navigate back to database list
 */

interface MySQLConnectionProps {
    onBack: () => void;
}

interface ConnectionType {
    id: 'user' | 'organization';
    label: string;
    selected: boolean;
}

const RadioButton = ({ selected }: { selected: boolean }) => (
    <div className="relative size-5 flex items-center justify-center">
        <div className={`w-5 h-5 rounded-full border-2 ${selected ? 'border-[#ac9578]' : 'border-neutral-300'}`}>
            {selected && (
                <div className="w-full h-full rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#ac9578]" />
                </div>
            )}
        </div>
    </div>
);

export const MySQLConnection = ({ onBack }: MySQLConnectionProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedConnection, setSelectedConnection] = useState<DatabaseConnection | null>(null);
    const [selectedConnectionId, setSelectedConnectionId] = useState<string | undefined>(undefined);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [connectionToDelete, setConnectionToDelete] = useState<string | null>(null);
    const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>([
        { id: 'user', label: 'CONNECT_FOR_INDIVIDUAL_ACCOUNT', selected: true },
        { id: 'organization', label: 'CONNECT_FOR_ORGANIZATION_ACCOUNT', selected: false }
    ]);

    const { data: connectionsList, isLoading: isLoadingList, refetch } = useListDatabaseConnections();
    const { data: connectionDetails, isLoading: isLoadingDetails, refetch: refetchDetails } = useGetDatabaseConnection(selectedConnectionId);
    const { mutate: upsertConnection, isPending: isUpserting } = useUpsertDatabaseConnection();
    const { mutate: deleteConnection, isPending: isDeleting } = useDeleteDatabaseConnection();
    const { mutate: connectConnection, isPending: isConnecting } = useConnectDatabaseConnection();
    const { mutate: disconnectConnection, isPending: isDisconnecting } = useDisconnectDatabaseConnection();

    // Filter only MySQL connections
    const mysqlConnections = useMemo(() =>
        connectionsList?.filter(conn => conn.DatabaseType === 'mysql') || [],
        [connectionsList]
    );

    // Auto-load connection details when mysql connection exists
    useEffect(() => {
        if (mysqlConnections.length > 0 && !selectedConnectionId && !isDialogOpen) {
            setSelectedConnectionId(mysqlConnections[0].ConnectionId);
        }
    }, [mysqlConnections, selectedConnectionId, isDialogOpen]);

    // Update selected scope based on existing connection
    useEffect(() => {
        if (mysqlConnections.length > 0) {
            const existingScope = mysqlConnections[0].Scope as 'user' | 'organization';
            setConnectionTypes(prev =>
                prev.map(type => ({ ...type, selected: type.id === existingScope }))
            );
        }
    }, [mysqlConnections]);

    const handleConnectionTypeSelect = (id: 'user' | 'organization') => {
        setConnectionTypes(prev =>
            prev.map(type => ({ ...type, selected: type.id === id }))
        );
    };

    const handleAddConfiguration = () => {
        setSelectedConnection(null);
        setSelectedConnectionId(undefined);
        setIsDialogOpen(true);
    };

    const handleEditConfiguration = (connectionId: string) => {
        setSelectedConnectionId(connectionId);
        setIsDialogOpen(true);
    };

    const handleConnect = async () => {
        // Get the selected scope
        const selectedScope = connectionTypes.find(type => type.selected)?.id || 'user';

        // If there's an existing connection, connect to it
        if (mysqlConnections.length > 0) {
            const connectionId = mysqlConnections[0].ConnectionId;
            connectConnection({ connectionId, scope: selectedScope }, {
                onSuccess: async () => {
                    toast({
                        variant: "success",
                        title: t('SUCCESS') || 'Success',
                        description: t('DATABASE_CONNECTED_SUCCESSFULLY') || 'Database connected successfully',
                    });
                    // Immediately refetch both list and details to update the UI
                    await Promise.all([refetch(), refetchDetails()]);
                },
                onError: (error: any) => {
                    console.error('Failed to connect database:', error);
                    toast({
                        variant: "destructive",
                        title: t('ERROR') || 'Error',
                        description: error?.message || t('FAILED_TO_CONNECT_DATABASE') || 'Failed to connect to database',
                    });
                }
            });
        } else {
            // If no connection exists, open the add configuration dialog
            handleAddConfiguration();
        }
    };

    const handleDisconnect = async () => {
        if (mysqlConnections.length > 0) {
            const connectionId = mysqlConnections[0].ConnectionId;
            disconnectConnection(connectionId, {
                onSuccess: async () => {
                    toast({
                        variant: "success",
                        title: t('SUCCESS') || 'Success',
                        description: t('DATABASE_DISCONNECTED_SUCCESSFULLY') || 'Database disconnected successfully',
                    });
                    // Immediately refetch both list and details to update the UI
                    await Promise.all([refetch(), refetchDetails()]);
                },
                onError: (error: any) => {
                    console.error('Failed to disconnect database:', error);
                    toast({
                        variant: "destructive",
                        title: t('ERROR') || 'Error',
                        description: error?.message || t('FAILED_TO_DISCONNECT_DATABASE') || 'Failed to disconnect from database',
                    });
                }
            });
        }
    };

    const handleDeleteClick = (connectionId: string) => {
        setConnectionToDelete(connectionId);
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (connectionToDelete) {
            deleteConnection(connectionToDelete, {
                onSuccess: () => {
                    toast({
                        variant: 'success',
                        title: t('SUCCESS') || 'Success',
                        description: t('CONNECTION_DELETED_SUCCESSFULLY') || 'Connection deleted successfully'
                    });
                    refetch();
                    setIsDeleteConfirmOpen(false);
                    setConnectionToDelete(null);
                },
                onError: (error) => {
                    toast({
                        variant: 'destructive',
                        title: t('ERROR') || 'Error',
                        description: t('FAILED_TO_DELETE_CONNECTION') || 'Failed to delete connection'
                    });
                    console.error('Delete connection error:', error);
                }
            });
        }
    };

    const handleSaveConfiguration = (formData: DatabaseFormData) => {
        const selectedScope = connectionTypes.find(type => type.selected)?.id || 'user';

        const payload = {
            databaseType: 'mysql' as const,
            scope: selectedScope,
            host: formData.host,
            port: formData.port,
            username: formData.username,
            password: formData.password,
            databaseName: formData.databaseName,
            connectionString: formData.connectionString || undefined,
            extra: null
        };

        upsertConnection(payload, {
            onSuccess: () => {
                toast({
                    variant: 'success',
                    title: t('SUCCESS') || 'Success',
                    description: selectedConnection
                        ? t('CONNECTION_UPDATED_SUCCESSFULLY') || 'Connection updated successfully'
                        : t('CONNECTION_CREATED_SUCCESSFULLY') || 'Connection created successfully'
                });
                refetch();
                setIsDialogOpen(false);
                setSelectedConnection(null);
                setSelectedConnectionId(undefined);
            },
            onError: (error) => {
                toast({
                    variant: 'destructive',
                    title: t('ERROR') || 'Error',
                    description: t('FAILED_TO_SAVE_CONNECTION') || 'Failed to save connection'
                });
                console.error('Upsert connection error:', error);
            }
        });
    };

    if (isLoadingList) {
        return (
            <div className="h-[560px] bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground">{t('LOADING') || 'Loading...'}</p>
                </div>
            </div>
        );
    }

    // When dialog opens and we're editing, wait for connection details to load
    if (isDialogOpen && selectedConnectionId && isLoadingDetails) {
        return (
            <div className="h-[560px] bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground">{t('LOADING_CONNECTION_DETAILS') || 'Loading connection details...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-background">
            <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 max-w-4xl">
                {/* Back Button */}
                <button
                    onClick={onBack}
                    className="flex items-center gap-[5px] text-[#ac9578] hover:text-[#9a8469] transition-colors"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-[14px] font-medium leading-[22px]">{t('BACK') || 'back'}</span>
                </button>

                {/* MySQL Header with Edit/Delete buttons */}
                <div className="pb-4 border-b border-light-gray">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-[48px] h-[48px] sm:w-[52px] sm:h-[52px] border border-light-gray rounded-lg flex items-center justify-center p-3">
                                <img src={mysqlIcon} alt="MySQL" className="w-full h-full object-contain" />
                            </div>
                            <div className="space-y-1 flex-1">
                                <h3 className="text-[15px] sm:text-[16px] font-bold text-foreground leading-[1.4]">
                                    MySQL
                                </h3>
                                <p className="text-[11px] sm:text-[12px] text-foreground leading-[1.4] tracking-[-0.24px]">
                                    {t('CONNECT_TO_MYSQL_DATABASE') || 'Connect to MySQL database'}
                                </p>
                            </div>
                        </div>

                        {/* Edit and Delete Icon buttons (only show if connection exists) */}
                        {mysqlConnections.length > 0 && (
                            <div className="flex gap-2 flex-shrink-0">
                                <button
                                    onClick={() => handleEditConfiguration(mysqlConnections[0].ConnectionId)}
                                    className="p-2 hover:bg-muted rounded-md transition-colors"
                                    title={t('EDIT_CONFIGURATION') || 'Edit Configuration'}
                                >
                                    <Edit className="h-5 w-5 text-foreground" />
                                </button>
                                <button
                                    onClick={() => handleDeleteClick(mysqlConnections[0].ConnectionId)}
                                    className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                                    title={t('DELETE_CONFIGURATION') || 'Delete Configuration'}
                                >
                                    <Trash2 className="h-5 w-5 text-destructive" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Empty State - Show when no configurations */}
                {mysqlConnections.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-border rounded-lg bg-muted/20">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                            <img src={mysqlIcon} alt="MySQL" className="w-8 h-8 object-contain" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {t('NO_CONFIGURATIONS') || 'No Configurations'}
                        </h3>
                        <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                            {t('NO_MYSQL_CONFIGURATIONS_MESSAGE') || 'You haven\'t added any MySQL configurations yet. Click the button below to get started.'}
                        </p>
                        <Button
                            onClick={handleAddConfiguration}
                            className="flex items-center gap-2 bg-[#ac9578] hover:bg-[#9a8469] text-white"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t('ADD_FIRST_CONFIGURATION') || 'Add First Configuration'}
                        </Button>
                    </div>
                )}

                {/* Show configuration sections only when configurations exist */}
                {mysqlConnections.length > 0 && (
                    <>
                        {/* Connection Type Selection (Scope) */}
                        <div className="space-y-3">
                            <h4 className="text-[13px] font-medium text-foreground leading-[1.25]">
                                {t('SELECT_CONNECTION_TYPE') || 'Select connection type'}
                            </h4>
                            <div className="flex flex-col sm:flex-row gap-3">
                                {connectionTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        onClick={() => handleConnectionTypeSelect(type.id)}
                                        className="flex-1 sm:max-w-[230px] p-3 sm:p-4 border border-light-gray rounded-lg flex items-center gap-2 hover:border-border transition-colors"
                                    >
                                        <RadioButton selected={type.selected} />
                                        <span className="text-[11px] sm:text-[12px] text-foreground leading-[1.4] tracking-[-0.24px] text-left">
                                            {t(type.label)}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Connection Type Warnings */}
                            {connectionTypes.find(type => type.id === 'user')?.selected && (
                                <div className="flex items-start gap-2 p-3 sm:p-4 bg-[hsl(var(--warning-background))] border border-[hsl(var(--warning))] rounded-lg">
                                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary-500))] flex-shrink-0 mt-0.5" />
                                    <p className="text-[11px] sm:text-[12px] text-[hsl(var(--warning-high-emphasis))] leading-[1.6] tracking-[-0.24px]">
                                        {t('ONLY_YOU_WILL_HAVE_ACCESS_TO_THE_CONNECTED_FILES') || 'Only you will have access to your connected files. They will not be shared with your organization'}
                                    </p>
                                </div>
                            )}

                            {connectionTypes.find(type => type.id === 'organization')?.selected && (
                                <div className="flex items-start gap-2 p-3 sm:p-4 bg-[hsl(var(--warning-background))] border border-[hsl(var(--warning))] rounded-lg">
                                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary-500))] flex-shrink-0 mt-0.5" />
                                    <p className="text-[11px] sm:text-[12px] text-[hsl(var(--warning-high-emphasis))] leading-[1.6] tracking-[-0.24px]">
                                        {t('AVAILABLE_TO_ALL_ORGANIZATION_MEMBERS') || 'Available to all organization members'}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Existing Configuration Display - Information Card Style */}
                        {connectionDetails ? (
                            <div className="space-y-3">
                                <h4 className="text-[15px] font-semibold text-foreground">
                                    {t('INFORMATION') || 'Information'}
                                </h4>
                                <div className="bg-card border border-border rounded-lg p-4">
                                    <div className="space-y-2.5">
                                        {/* Display Name */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                {t('DATABASE_NAME') || 'Database Name'}
                                            </span>
                                            <span className="text-sm text-foreground font-medium">
                                                {mysqlConnections[0].DisplayName}
                                            </span>
                                        </div>

                                        {/* Host */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                {t('HOST') || 'Host'}
                                            </span>
                                            <span className="text-sm text-foreground font-medium">
                                                {connectionDetails.connectionInfo.host}
                                            </span>
                                        </div>

                                        {/* Port */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                {t('PORT') || 'Port'}
                                            </span>
                                            <span className="text-sm text-foreground font-medium">
                                                {connectionDetails.connectionInfo.port}
                                            </span>
                                        </div>

                                        {/* Username */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                {t('USERNAME') || 'Username'}
                                            </span>
                                            <span className="text-sm text-foreground font-medium">
                                                {connectionDetails.connectionInfo.username}
                                            </span>
                                        </div>

                                        {/* Database */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                {t('DATABASE') || 'Database'}
                                            </span>
                                            <span className="text-sm text-foreground font-medium">
                                                {connectionDetails.connectionInfo.databaseName}
                                            </span>
                                        </div>

                                        {/* Connection String - with external link icon */}
                                        {connectionDetails.connectionInfo.connectionString && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-muted-foreground">
                                                    {t('CONNECTION_STRING') || 'Connection String'}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-sm text-foreground font-medium truncate max-w-[200px]">
                                                        {connectionDetails.connectionInfo.connectionString}
                                                    </span>
                                                    <ExternalLink className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                                                </div>
                                            </div>
                                        )}

                                        {/* Status */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                {t('STATUS') || 'Status'}
                                            </span>
                                            <span className={`text-sm font-medium ${connectionDetails.isActive ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground'}`}>
                                                {connectionDetails.isActive ? t('ACTIVE') || 'Active' : t('INACTIVE') || 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : isLoadingDetails ? (
                            // Skeleton loader while loading connection details
                            <div className="space-y-3">
                                <h4 className="text-[15px] font-semibold text-foreground">
                                    {t('INFORMATION') || 'Information'}
                                </h4>
                                <div className="bg-card border border-border rounded-lg p-4">
                                    <div className="space-y-2.5">
                                        {/* Skeleton rows */}
                                        {[1, 2, 3, 4, 5, 6].map((item) => (
                                            <div key={item} className="flex items-center justify-between">
                                                <div className="h-4 bg-muted rounded w-24 animate-pulse"></div>
                                                <div className="h-4 bg-muted rounded w-32 animate-pulse"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {/* Footer with Connect/Disconnect Button */}
                        <div className="pt-4 border-t border-light-gray flex justify-center sm:justify-end gap-3">
                            {mysqlConnections.length > 0 && mysqlConnections[0].IsActive ? (
                                <Button
                                    onClick={handleDisconnect}
                                    disabled={isDisconnecting}
                                    className="min-w-[120px] bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                                >
                                    {isDisconnecting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                            {t('DISCONNECTING') || 'Disconnecting...'}
                                        </>
                                    ) : (
                                        t('DISCONNECT') || 'Disconnect'
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleConnect}
                                    disabled={isConnecting}
                                    className="min-w-[120px] bg-[#ac9578] hover:bg-[#9a8469] text-white"
                                >
                                    {isConnecting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                            {t('CONNECTING') || 'Connecting...'}
                                        </>
                                    ) : (
                                        t('CONNECT') || 'Connect'
                                    )}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Add/Edit Configuration Dialog */}
            <DatabaseConfigDialog
                isOpen={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                    setSelectedConnection(null);
                    setSelectedConnectionId(undefined);
                }}
                onSave={handleSaveConfiguration}
                existingConnection={connectionDetails || selectedConnection}
                databaseType="mysql"
                isLoading={isUpserting}
            />

            {/* Delete Confirmation Dialog */}
            {isDeleteConfirmOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-background rounded-lg shadow-lg max-w-md w-full p-6">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-destructive" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {t('DELETE_CONFIGURATION') || 'Delete Configuration'}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                    {t('DELETE_CONFIGURATION_CONFIRMATION') || 'Are you sure you want to delete this configuration? This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsDeleteConfirmOpen(false);
                                    setConnectionToDelete(null);
                                }}
                                disabled={isDeleting}
                            >
                                {t('CANCEL') || 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                        {t('DELETING') || 'Deleting...'}
                                    </>
                                ) : (
                                    t('DELETE') || 'Delete'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
